import { useState } from "react";
import { Package } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface BatchCommandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (start: number, end: number) => Promise<any>;
}

export function BatchCommandDialog({
  open,
  onOpenChange,
  onSubmit,
}: BatchCommandDialogProps) {
  const [startNumber, setStartNumber] = useState("1");
  const [endNumber, setEndNumber] = useState("50");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const start = parseInt(startNumber) || 0;
  const end = parseInt(endNumber) || 0;
  const count = end >= start ? end - start + 1 : 0;

  const handleSubmit = async () => {
    if (count <= 0 || count > 200) return;

    setIsSubmitting(true);
    try {
      await onSubmit(start, end);
      setStartNumber("1");
      setEndNumber("50");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Criar Comandas em Lote
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startNumber">De (número)</Label>
              <Input
                id="startNumber"
                type="number"
                min="1"
                value={startNumber}
                onChange={(e) => setStartNumber(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endNumber">Até (número)</Label>
              <Input
                id="endNumber"
                type="number"
                min="1"
                value={endNumber}
                onChange={(e) => setEndNumber(e.target.value)}
              />
            </div>
          </div>

          {count > 0 && count <= 200 && (
            <div className="rounded-lg bg-muted p-3 text-center">
              <p className="text-sm">
                Serão criadas <span className="font-bold">{count}</span> comandas
              </p>
              <p className="text-xs text-muted-foreground">
                Numeradas de {String(start).padStart(3, "0")} até {String(end).padStart(3, "0")}
              </p>
            </div>
          )}

          {count > 200 && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Máximo de 200 comandas por vez.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || count <= 0 || count > 200}
          >
            {isSubmitting ? "Criando..." : `Criar ${count} Comandas`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
